import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { abTrack, useSeen } from "@/lib/ab";
import { UpgradeCard, CheckingCard, LockedCard, isChecking, useLang, clickUpgrade } from "./FreeCards";
import { FreeIdeaContent } from "./FreeIdea";
import { useScrollUpsell, useHalfScrollPopup, DashboardUnlockPopup } from "./UpsellPopup";

export { isChecking };

const D = {
  en: {
    welcome: (n) => `Welcome to Sidebizz${n ? `, ${n}` : ""}!`, intro: "Here is your FREE business idea. Read it below — it’s all yours.",
    lockT: "Want to see this business idea?", lockChecking: "We’re checking your payment. This idea will open as soon as your payment is confirmed.",
    lockD: (p) => `Unlock all 150+ business ideas for just Rs ${p}. Pay once and explore all the ideas!`, lockCta: (p) => `Unlock 150+ Ideas – Rs ${p}`,
    more: "More business ideas", locked: "locked",
  },
  fr: {
    welcome: (n) => `Bienvenue sur Sidebizz${n ? `, ${n}` : ""} !`, intro: "Voici ton idée de business GRATUITE. Lis-la ci-dessous, elle est à toi.",
    lockT: "Tu veux voir cette idée de business ?", lockChecking: "Nous vérifions ton paiement. Cette idée s’ouvrira dès que ton paiement sera confirmé.",
    lockD: (p) => `Débloque les 150+ idées de business pour seulement Rs ${p}. Paie une fois et explore toutes les idées !`, lockCta: (p) => `Débloquer les 150+ idées – Rs ${p}`,
    more: "Autres idées de business", locked: "verrouillées",
  },
};

function LockedDialog({ open, onClose, price, checking }) {
  const lang = useLang();
  const t = D[lang];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[90vw] max-w-[380px] rounded-3xl bg-white p-6 text-center" data-testid="locked-idea-dialog">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-[#EAF8F4] flex items-center justify-center"><Lock className="h-6 w-6 text-[#0BAF9A]" /></div>
        <DialogTitle className="font-display font-extrabold text-xl text-[#102B3F]">{t.lockT}</DialogTitle>
        {checking ? <DialogDescription className="text-[15px]">{t.lockChecking}</DialogDescription> : (
          <>
            <DialogDescription className="text-[15px]">{t.lockD(price)}</DialogDescription>
            <Link to="/unlock" onClick={() => clickUpgrade(lang, "locked_card")} className="lp-btn w-full" data-testid="locked-dialog-unlock-btn">{t.lockCta(price)} <ArrowRight className="h-4 w-4" /></Link>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function FreeDashboard({ data }) {
  const { user } = useAuth();
  const lang = useLang();
  const t = D[lang];
  const [locked, setLocked] = useState(false);
  const checking = isChecking(data.request);
  const [popup, setPopup] = useHalfScrollPopup(!checking);
  useScrollUpsell("free-idea-steps", false);
  useEffect(() => { abTrack("dashboard_loaded", { lang }); abTrack("free_idea_displayed", { lang }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useSeen("free-upgrade-section", () => abTrack("upgrade_offer_viewed", { lang, props: { where: "upgrade_section" } }), !checking);
  const openLocked = () => { setLocked(true); if (!checking) abTrack("upgrade_offer_viewed", { lang, props: { where: "locked_card" } }); };
  return (
    <div className="space-y-6" data-testid="free-dashboard">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-[#102B3F]" data-testid="free-welcome">{t.welcome(user.first_name)}</h1>
        <p className="text-[16px] text-[#4A5D6A] mt-2">{t.intro}</p>
      </div>
      <Link to="/dashboard/challenge" className="flex items-center justify-between gap-3 rounded-2xl bg-[#13283A] text-white px-4 py-3 min-h-[56px]" data-testid="free-challenge-banner">
        <span className="text-sm font-bold">{lang === "fr" ? "Nouveau : le Challenge Sidebizz 5 jours, gratuit" : "New: the free 5-day Sidebizz Challenge (in French)"}</span>
        <ArrowRight className="h-5 w-5 text-[#13B698] shrink-0" />
      </Link>
      <FreeIdeaContent data={data} />
      {checking ? <CheckingCard /> : <UpgradeCard price={data.price} rejected={data.request?.payment_status === "rejected"} />}
      <section>
        <h2 className="font-display font-bold text-lg text-[#102B3F]">{t.more} <span className="text-sm text-[#7D8C96] font-semibold">({data.locked.length} {t.locked})</span></h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-3" data-testid="locked-ideas-grid">
          {data.locked.map((i) => <LockedCard key={i.number} idea={i} onClick={openLocked} />)}
        </div>
      </section>
      <DashboardUnlockPopup open={popup} onClose={() => setPopup(false)} price={data.price} total={data.total} />
      <LockedDialog open={locked} onClose={() => setLocked(false)} price={data.price} checking={checking} />
    </div>
  );
}
